import { useEffect, useState } from 'react';
import usePrevious from '../../../helpers/usePrevious';
import { readStorage, writeStorage } from '../storage/browserStorage';
import { resetStickySearchInAssetbank } from '../helpers/searchUtils';
import { useCanUseSimilaritySearch } from './useCanUseSimilaritySearch';

/** Context for AI: keyword↔AI toggle; the on/off choice persists in localStorage, but effective NLS also requires the ai_similarity_search ECA (canUseSimilaritySearch). Toggling resets the currently-applied sticky/NLS filter in the assetbank. On a Unified Search PoC portal the choice lives under its own key and defaults ON. */
export const useNLSSearch = (isStickySearch: boolean, isHybridSearchPortal = false) => {
	const storageName = isHybridSearchPortal ? 'hybridSearchOn' : 'nlsSearchOn';

	const [isNLSSearchOn, setIsNLSSearchOn] = useState<boolean>(() => readStorage(storageName));

	const { canUseSimilaritySearch } = useCanUseSimilaritySearch();

	const isPerformingNLSSearch = canUseSimilaritySearch && isNLSSearchOn;

	useEffect(() => {
		writeStorage(storageName, isNLSSearchOn);
	}, [isNLSSearchOn, storageName]);

	const previousIsPerformingNLSSearch = usePrevious(isPerformingNLSSearch);
	// On the first render there is no previous mode, so nothing has been switched yet.
	const hasSwitchedSearchMode =
		previousIsPerformingNLSSearch !== undefined &&
		previousIsPerformingNLSSearch !== isPerformingNLSSearch;

	useEffect(() => {
		if (!isStickySearch || !hasSwitchedSearchMode) {
			return;
		}

		// Under hybrid search, the NLS/Hybrid toggle never applies a similarity filter, because the hybrid endpoint
		// works directly with `text` and `singletext` instead of `similarToText`. So what needs clearing is
		// always the text filter. Under NLS search: "!" because if now it is doing an NLS search, it means
		// that what was previously being applied (and hence what we need to reset) was NOT an NLS
		// search (and viceversa)
		resetStickySearchInAssetbank(
			isHybridSearchPortal && isPerformingNLSSearch,
			isHybridSearchPortal ? false : !isPerformingNLSSearch,
		);
	}, [isStickySearch, hasSwitchedSearchMode, isPerformingNLSSearch, isHybridSearchPortal]);

	return { canUseSimilaritySearch, isNLSSearchOn, setIsNLSSearchOn, isPerformingNLSSearch };
};
